import { useToast } from "@/components/ui/use-toast";
import { useApolloClient } from "@apollo/client";
import { validateForm } from "@common/components/utils/FormValidation";
import {
  OrganizationUserDocument,
  OrganizationUserQuery,
  OrganizationUsersDocument,
  UserRole,
  useInviteEmployeeMutation,
  useTerminateEmployeeProfileMutation,
  useUpdateEmployeeMutation,
} from "@common/utils/graphql";
import { UseFormReturn } from "react-hook-form";
import { CategoryUploadSchema } from "../categories/forms";
import { z } from "zod";
import { EmployeeFormSchema } from "../forms/Forms";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import { useSetRecoilState } from "recoil";
import {
  availableRoles,
  getHighestRole,
  roleState,
} from "@common/state/roleState";
import { useState } from "react";
import { useCreateRequest } from "@common/utils/rest/createRequest";

type UseUploadUserProps = {
  form: UseFormReturn<z.infer<typeof CategoryUploadSchema>>;
  onSucces: () => void;
};

export const useUploadUser = ({ form, onSucces }: UseUploadUserProps) => {
  const { toast } = useToast();
  const { createRequest } = useCreateRequest();
  const [isUploading, setIsUploading] = useState(false);
  const uploadPersonCSV = async () => {
    const isFormValid = await validateForm(form);
    if (!isFormValid) {
      return;
    }

    setIsUploading(true);
    const { file } = form.getValues();
    const formData = new FormData();
    formData.append("file", file || "");

    try {
      const res = await createRequest(
        "employees/consumeCSVFile",
        "POST",
        formData,
      );
      if (res.status >= 400 || !res.ok) {
        throw new Error();
      }
      toast({
        title: "CSV uploaded successfully",
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Failed to upload CSV, please try again",
      });
    } finally {
      setIsUploading(false);
    }

    onSucces();
  };
  return [uploadPersonCSV, { loading: isUploading }] as const;
};

type UseInviteOrUpdateUserProps = {
  form: UseFormReturn<z.infer<typeof EmployeeFormSchema>>;
  variables: { userId?: string };
  onSucces: () => void;
};

export const useInviteOrUpdateUser = ({
  form,
  variables,
  onSucces,
}: UseInviteOrUpdateUserProps) => {
  const { toast } = useToast();
  const setRole = useSetRecoilState(roleState);
  const setRoles = useSetRecoilState(availableRoles);
  const currentUserId = useUserStore((state) => state.userId);
  const [
    adminOrManagerInviteEmployee,
    { loading: adminOrManagerInviteEmployeeLoading },
  ] = useInviteEmployeeMutation({
    refetchQueries: [OrganizationUserDocument, OrganizationUsersDocument],
  });
  const [
    adminOrManagerUpdateEmployee,
    { loading: adminOrManagerUpdateEmployeeLoading },
  ] = useUpdateEmployeeMutation({
    refetchQueries: [OrganizationUserDocument, OrganizationUsersDocument],
  });
  const inviteOrUpdateUser = async (updateUser?: boolean) => {
    const isFormValid = await validateForm(form);
    if (!isFormValid) {
      return;
    }

    const values = form.getValues();
    try {
      if (updateUser) {
        const { data } = await adminOrManagerUpdateEmployee({
          variables: {
            userId: variables.userId!,
            firstName: values.firstName,
            lastName: values.lastName,
            managerEmployeeProfileId: values.managerEmployeeProfileId,
            departmentId: Number(values.departmentId),
            roles: values.roles as UserRole[],
          },
        });
        if (
          data?.updateEmployee.__typename !== "MutationUpdateEmployeeSuccess"
        ) {
          throw new Error();
        }

        // If an admin removes their admin role, update role and roles state
        // to redirect them to the correct route
        if (
          currentUserId === variables.userId &&
          !values.roles.includes(UserRole.Admin)
        ) {
          setRole(getHighestRole(values.roles as UserRole[]));
          setRoles(values.roles as UserRole[]);
        }
      } else {
        const { data } = await adminOrManagerInviteEmployee({
          variables: {
            firstName: values.firstName,
            lastName: values.lastName,
            workEmail: values.workEmail,
            managerEmployeeProfileId: values.managerEmployeeProfileId,
            departmentId: Number(values.departmentId),
            roles: values.roles as UserRole[],
          },
        });
        if (
          data?.inviteEmployee.__typename !== "MutationInviteEmployeeSuccess"
        ) {
          throw new Error();
        }
      }
      onSucces();
    } catch (e) {
      const title = updateUser
        ? "Failed to update user, please try again"
        : `Failed to send invite to ${values.workEmail}, please try again`;
      toast({
        title,
        variant: "destructive",
      });
    }
  };
  return [
    inviteOrUpdateUser,
    {
      loading:
        adminOrManagerInviteEmployeeLoading ||
        adminOrManagerUpdateEmployeeLoading,
    },
  ] as const;
};

type UseTeminateEmployeeProfileProps = {
  variables: {
    userId: string;
  };
  onSucces: () => void;
};

export const useTerminateEmployeeProfile = ({
  variables,
  onSucces,
}: UseTeminateEmployeeProfileProps) => {
  const client = useApolloClient();
  const { toast } = useToast();
  const [adminTerminateEmployeeProfile, { loading }] =
    useTerminateEmployeeProfileMutation({
      refetchQueries: [OrganizationUsersDocument],
    });
  const terminateEmployeeProfile = async () => {
    try {
      const { data } = await adminTerminateEmployeeProfile({
        variables,
      });

      if (
        data?.terminateEmployeeProfile.__typename !==
        "MutationTerminateEmployeeProfileSuccess"
      ) {
        throw new Error();
      } else {
        const terminatedUser = data.terminateEmployeeProfile.data;
        client.cache.updateQuery<OrganizationUserQuery>(
          { query: OrganizationUserDocument, variables },
          (cacheData) => {
            if (
              cacheData?.organizationUser.__typename ===
              "QueryOrganizationUserSuccess"
            ) {
              return {
                organizationUser: {
                  ...cacheData.organizationUser,
                  data: {
                    ...cacheData.organizationUser.data,
                    ...terminatedUser,
                  },
                },
              };
            }
          },
        );
        toast({
          title: "User profile terminated successfully",
        });
        onSucces();
      }
    } catch (e) {
      toast({
        title: "Failed to terminate user, please try again",
        variant: "destructive",
      });
    }
  };

  return [terminateEmployeeProfile, { loading }] as const;
};
